/**
 * TypeScript interfaces for Lending API domain models
 * Used by both mock and real API implementations
 */

// Core domain enums (matching database schema)
export enum LoanStatus {
  PENDING = 'pending',
  UNDER_REVIEW = 'under_review',
  APPROVED = 'approved',
  REJECTED = 'rejected',
  FUNDED = 'funded',
  ACTIVE = 'active',
  PAID_OFF = 'paid_off',
  DEFAULTED = 'defaulted',
}

export enum CollateralType {
  REAL_ESTATE = 'real_estate',
  VEHICLE = 'vehicle',
  EQUIPMENT = 'equipment',
  INVENTORY = 'inventory',
  ACCOUNTS_RECEIVABLE = 'accounts_receivable',
  OTHER = 'other',
}

export enum WorkflowStatus {
  INITIATED = 'initiated',
  IN_PROGRESS = 'in_progress',
  PENDING_APPROVAL = 'pending_approval',
  APPROVED = 'approved',
  REJECTED = 'rejected',
  COMPLETED = 'completed',
  CANCELLED = 'cancelled',
}

export enum EntityType {
  COMMUNITY = 'community',
  RESIDENT = 'resident',
  BANK = 'bank',
  VENDOR = 'vendor',
  ADMIN = 'admin',
}

// Core domain models
export interface Community {
  id: string;
  name: string;
  address: string;
  city: string;
  state: string;
  zip_code: string;
  country: string;
  total_units: number;
  occupied_units: number;
  community_type: string;
  established_date: string;
  management_company?: string;
  contact_email: string;
  contact_phone?: string;
  website?: string;
  amenities: string[];
  created_at: string;
  updated_at: string;
}

export interface Resident {
  id: string;
  community_id: string;
  user_id: string;
  unit_number: string;
  lease_start_date: string;
  lease_end_date?: string;
  rent_amount: number;
  security_deposit: number;
  move_in_date: string;
  move_out_date?: string;
  resident_status: 'active' | 'inactive' | 'pending';
  emergency_contact_name?: string;
  emergency_contact_phone?: string;
  created_at: string;
  updated_at: string;
}

export interface CollateralAsset {
  id: string;
  community_id: string;
  asset_type: CollateralType;
  asset_description: string;
  estimated_value: number;
  appraisal_date?: string;
  appraisal_value?: number;
  condition_rating: number; // 1-10 scale
  location?: string;
  ownership_documentation?: string;
  created_at: string;
  updated_at: string;
}

export interface BankPartner {
  id: string;
  bank_name: string;
  bank_code?: string;
  contact_person: string;
  contact_email: string;
  contact_phone?: string;
  address: string;
  city: string;
  state: string;
  zip_code: string;
  country: string;
  website?: string;
  partnership_status: 'active' | 'inactive' | 'pending';
  partnership_start_date: string;
  partnership_end_date?: string;
  created_at: string;
  updated_at: string;
}

export interface BankLendingCriteria {
  id: string;
  bank_partner_id: string;
  min_loan_amount: number;
  max_loan_amount: number;
  min_credit_score?: number;
  max_loan_to_value_ratio: number;
  min_debt_service_coverage_ratio?: number;
  acceptable_collateral_types: CollateralType[];
  geographic_restrictions: string[];
  created_at: string;
  updated_at: string;
}

export interface BankUser {
  id: string;
  bank_partner_id: string;
  user_id: string;
  role: 'underwriter' | 'loan_officer' | 'risk_analyst' | 'portfolio_manager' | 'admin';
  permissions: string[];
  is_active: boolean;
  last_login?: string;
  created_at: string;
  updated_at: string;
}

export interface CommunityLoanProposal {
  id: string;
  community_id: string;
  requesting_resident_id?: string;
  loan_amount: number;
  loan_purpose: string;
  requested_term_months: number;
  proposed_interest_rate?: number;
  collateral_asset_ids: string[];
  loan_status: LoanStatus;
  submitted_date: string;
  review_deadline?: string;
  approved_date?: string;
  funded_date?: string;
  rejection_reason?: string;
  created_at: string;
  updated_at: string;
}

export interface CrossEntityWorkflow {
  id: string;
  workflow_type: string;
  initiating_entity_type: EntityType;
  initiating_entity_id: string;
  target_entity_type: EntityType;
  target_entity_id: string;
  related_proposal_id?: string;
  workflow_status: WorkflowStatus;
  current_step: string;
  workflow_data: Record<string, unknown>;
  started_at: string;
  completed_at?: string;
  created_at: string;
  updated_at: string;
}

export interface WorkflowNotification {
  id: string;
  workflow_id: string;
  recipient_entity_type: EntityType;
  recipient_entity_id: string;
  notification_type: string;
  title: string;
  message: string;
  is_read: boolean;
  action_required: boolean;
  action_deadline?: string;
  created_at: string;
  updated_at: string;
}

// Aggregate/derived models for API responses
export interface LoanProposalWithDetails extends CommunityLoanProposal {
  community: Community;
  requesting_resident?: Resident;
  collateral_assets: CollateralAsset[];
  workflows: CrossEntityWorkflow[];
}

export interface BankProposalQueue {
  proposals: LoanProposalWithDetails[];
  total_count: number;
  pending_count: number;
  under_review_count: number;
  overdue_count: number;
}

export interface RiskAssessment {
  proposal_id: string;
  credit_score?: number;
  debt_to_income_ratio?: number;
  loan_to_value_ratio: number;
  collateral_coverage_ratio: number;
  community_financial_health: number; // 1-10 scale
  risk_rating: 'low' | 'medium' | 'high' | 'very_high';
  risk_factors: string[];
  recommendations: string[];
  assessed_by?: string;
  assessed_at: string;
}

export interface PortfolioSummary {
  total_active_loans: number;
  total_loan_value: number;
  average_loan_size: number;
  total_monthly_payments: number;
  default_rate: number;
  portfolio_yield: number;
  loans_by_status: Record<LoanStatus, number>;
  loans_by_community: Array<{
    community_id: string;
    community_name: string;
    loan_count: number;
    total_value: number;
  }>;
}

export interface FinancialMetrics {
  portfolio_summary: PortfolioSummary;
  monthly_originations: Array<{
    month: string;
    loan_count: number;
    total_value: number;
  }>;
  risk_distribution: Array<{
    risk_rating: string;
    loan_count: number;
    total_value: number;
  }>;
  performance_metrics: {
    loans_30_days_past_due: number;
    loans_60_days_past_due: number;
    loans_90_days_past_due: number;
    charge_offs_ytd: number;
    net_interest_margin: number;
  };
}

// API request/response wrapper types
export interface ApiResponse<T> {
  data: T;
  success: boolean;
  message?: string;
  timestamp: string;
}

export interface PaginatedResponse<T> {
  data: T[];
  pagination: {
    page: number;
    limit: number;
    total: number;
    total_pages: number;
  };
  success: boolean;
  message?: string;
  timestamp: string;
}

// API endpoint parameter types
export interface GetProposalQueueParams {
  bank_id: string;
  status?: LoanStatus[];
  page?: number;
  limit?: number;
  sort_by?: 'submitted_date' | 'loan_amount' | 'review_deadline';
  sort_order?: 'asc' | 'desc';
}

export interface CreateLoanProposalParams {
  community_id: string;
  requesting_resident_id?: string;
  loan_amount: number;
  loan_purpose: string;
  requested_term_months: number;
  collateral_asset_ids: string[];
}

export interface UpdateProposalStatusParams {
  proposal_id: string;
  new_status: LoanStatus;
  rejection_reason?: string;
  approved_terms?: {
    interest_rate: number;
    term_months: number;
    fees: Record<string, number>;
  };
}
